import { Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { CrossTabChange } from '@/lib/crossTabAuth'

interface CrossTabIdentityBannerProps {
  change: Exclude<CrossTabChange, { kind: 'user' }>
}

/**
 * Sticky, non-dismissible notice (ReadOnlyBanner shape) shown when another tab
 * changed this tab's identity (#301). It occupies its own row rather than
 * overlaying content.
 * - tenant switch by the same user: role=status, Reload.
 * - signed out elsewhere: role=alert, Sign in (no immediate navigation, so the
 *   screen the user was on is not yanked away).
 */
export function CrossTabIdentityBanner({ change }: CrossTabIdentityBannerProps) {
  const signedOut = change.kind === 'signed_out'
  return (
    <div
      role={signedOut ? 'alert' : 'status'}
      data-testid="cross-tab-identity-banner"
      className="flex flex-wrap items-center gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200"
    >
      <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="flex-1">
        {signedOut
          ? 'You signed out in another tab. Changes are paused here. Sign in to continue.'
          : `Your organization changed in another tab.${change.tenantName ? ` You're now in ${change.tenantName}.` : ''} Changes are paused here. Reload to continue.`}
      </span>
      {signedOut ? (
        <Button size="sm" onClick={() => window.location.assign('/login')}>
          Sign in
        </Button>
      ) : (
        <Button size="sm" onClick={() => window.location.reload()}>
          Reload
        </Button>
      )}
    </div>
  )
}
